import {
  Alert,
  Badge,
  Button,
  Group,
  SimpleGrid,
  Stack,
  Table,
  Text,
  Title,
} from "@mantine/core";
import {
  IconCash,
  IconClock,
  IconCoin,
  IconUserCheck,
  IconUsers,
  IconUsersGroup,
} from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  ContentCard,
  DataTableShell,
  LoadingOverlay,
  PageHeader,
  SortableTh,
  StatCard,
  useInfinitePage,
  useTableSort,
} from "../../components/ui";
import { NAV_ICONS } from "../../lib/navCopy";
import { useAuth } from "../../context/useAuth";
import { isIdentityVerified } from "../../lib/accountVerification";
import { KycStatusCallout } from "../../components/KycStatusCallout";
import { affiliateApi, earningsApi } from "../../lib/api";
import { formatCities, formatDate, formatPaise, statusColor, STATUS_LABELS } from "../../lib/format";

export function AffiliateDashboardPage() {
  const { user } = useAuth();
  const identityOk = isIdentityVerified(user);

  const profileQuery = useQuery({
    queryKey: ["affiliate-profile"],
    queryFn: () => affiliateApi.myProfile(),
  });

  const statsQuery = useQuery({
    queryKey: ["affiliate-stats"],
    queryFn: () => affiliateApi.stats(),
  });

  const earningsQuery = useQuery({
    queryKey: ["affiliate-earnings-summary"],
    queryFn: earningsApi.summary,
  });

  const candidatesQuery = useInfinitePage(["affiliate-candidates"], (page, pageSize) =>
    affiliateApi.listCandidates({ page, page_size: pageSize }),
  );

  const candidates = candidatesQuery.items;

  const { sorted: sortedCandidates, sortKey, direction, toggle } = useTableSort(candidates, {
    name: (c) => c.full_name,
    city: (c) => formatCities(c.cities?.length ? c.cities : c.city ? [c.city] : []),
    status: (c) => STATUS_LABELS[c.overall_status] ?? c.overall_status,
    latestJob: (c) => {
      const latest = c.submissions[0];
      return latest ? `${latest.job_title} · ${latest.company_name}` : null;
    },
    joined: (c) => new Date(c.joined_at),
  });

  if (profileQuery.isLoading) {
    return <LoadingOverlay height="var(--axio-unit-240)" />;
  }

  const profile = profileQuery.data;

  return (
    <Stack gap="lg">
      <PageHeader
        icon={NAV_ICONS.dashboard}
        title="Affiliate dashboard"
        subtitle="Share your link and earn a sourcing fee when referred candidates are placed."
      />

      <KycStatusCallout profile={profile} jobsPath="/affiliate/jobs" variant="affiliate" />

      {!identityOk && (
        <Alert color="orange" title="Verify your email and phone">
          You must verify your email and phone before sharing a candidate link.{" "}
          <Text component={Link} to="/affiliate/profile" span fw="var(--axio-font-weight-semibold)" c="orange.8" td="underline">
            Go to Profile
          </Text>
        </Alert>
      )}

      <ContentCard>
        <Stack gap="var(--axio-unit-4)">
          <Text fw="var(--axio-font-weight-bold)" size="lg">
            {profile?.user.full_name}
          </Text>
          <Text size="sm" c="dimmed">
            {profile?.user.email}
            {profile?.city ? ` · ${profile.city}` : ""}
          </Text>
          <Group gap="var(--axio-unit-6)" mt="var(--axio-unit-6)">
            <Badge color="teal" variant="light">
              {profile?.status ?? "active"}
            </Badge>
          </Group>
        </Stack>
      </ContentCard>

      <Alert color="teal" variant="light" title="How it works">
        Candidates who apply through your link join the AxioHire platform pool with your attribution.
        When they are hired, you earn the sourcing share of the placement fee.
      </Alert>

      <SimpleGrid cols={{ base: 2, sm: 4 }}>
        <StatCard
          label="Referred"
          value={statsQuery.data?.total_referred ?? 0}
          icon={IconUsers}
          color="teal"
        />
        <StatCard
          label="In pipeline"
          value={statsQuery.data?.in_pipeline ?? 0}
          icon={IconClock}
          color="yellow"
        />
        <StatCard
          label="Hired"
          value={statsQuery.data?.hired ?? 0}
          icon={IconUserCheck}
          color="green"
        />
        <StatCard
          label="In talent pool"
          value={statsQuery.data?.in_pool ?? 0}
          icon={IconUsersGroup}
          color="teal"
        />
      </SimpleGrid>

      <Group justify="space-between" align="flex-end">
        <div>
          <Title order={4}>Payments</Title>
          <Text size="sm" c="dimmed">
            Sourcing fees from hires you referred
          </Text>
        </div>
        <Button component={Link} to="/affiliate/payments" variant="light" color="teal" size="compact-sm">
          View all payments
        </Button>
      </Group>

      <SimpleGrid cols={{ base: 1, sm: 3 }}>
        <Link to="/affiliate/payments" style={{ textDecoration: "none", color: "inherit" }}>
          <StatCard
            label="Pending"
            value={formatPaise(earningsQuery.data?.pending_paise ?? 0)}
            icon={IconClock}
            color="teal"
            trend={
              <Text size="sm" c="dimmed" mt="var(--axio-unit-4)">
                {earningsQuery.data?.pending_count ?? 0} payout
                {(earningsQuery.data?.pending_count ?? 0) === 1 ? "" : "s"} awaiting release
              </Text>
            }
          />
        </Link>
        <Link to="/affiliate/payments" style={{ textDecoration: "none", color: "inherit" }}>
          <StatCard
            label="Received"
            value={formatPaise(earningsQuery.data?.received_paise ?? 0)}
            icon={IconCash}
            color="green"
            trend={
              <Text size="sm" c="dimmed" mt="var(--axio-unit-4)">
                {earningsQuery.data?.received_count ?? 0} payout
                {(earningsQuery.data?.received_count ?? 0) === 1 ? "" : "s"} paid out
              </Text>
            }
          />
        </Link>
        <Link to="/affiliate/payments" style={{ textDecoration: "none", color: "inherit" }}>
          <StatCard
            label="Lifetime"
            value={formatPaise(earningsQuery.data?.lifetime_paise ?? 0)}
            icon={IconCoin}
            trend={
              <Text size="sm" c="dimmed" mt="var(--axio-unit-4)">
                Pending + received
              </Text>
            }
          />
        </Link>
      </SimpleGrid>

      <Stack gap="sm">
        <Text fw="var(--axio-font-weight-bold)" size="lg">
          Candidates who joined via your link
        </Text>
        <DataTableShell
          loading={candidatesQuery.isLoading}
          error={
            candidatesQuery.isError
              ? candidatesQuery.error instanceof Error
                ? candidatesQuery.error.message
                : "Failed to load candidates"
              : undefined
          }
          onRetry={() => void candidatesQuery.refetch()}
          hasMore={candidatesQuery.hasMore}
          loadingMore={candidatesQuery.loadingMore}
          onLoadMore={candidatesQuery.loadMore}
          isEmpty={candidates.length === 0}
          empty={{
            icon: IconUsers,
            title: "No candidates yet",
            description: "Share your link to start referring candidates.",
          }}
        >
          <Table verticalSpacing="md" highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <SortableTh columnKey="name" sortKey={sortKey} direction={direction} onSort={toggle}>
                  Name
                </SortableTh>
                <SortableTh columnKey="city" sortKey={sortKey} direction={direction} onSort={toggle}>
                  City
                </SortableTh>
                <SortableTh columnKey="status" sortKey={sortKey} direction={direction} onSort={toggle}>
                  Status
                </SortableTh>
                <SortableTh columnKey="latestJob" sortKey={sortKey} direction={direction} onSort={toggle}>
                  Latest job
                </SortableTh>
                <SortableTh columnKey="joined" sortKey={sortKey} direction={direction} onSort={toggle}>
                  Joined
                </SortableTh>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {sortedCandidates.map((c) => {
                const latest = c.submissions[0];
                return (
                  <Table.Tr key={c.id}>
                    <Table.Td fw="var(--axio-font-weight-medium)">{c.full_name}</Table.Td>
                    <Table.Td>{formatCities(c.cities?.length ? c.cities : c.city ? [c.city] : [])}</Table.Td>
                    <Table.Td>
                      <Badge color={statusColor(c.overall_status)} variant="light">
                        {STATUS_LABELS[c.overall_status] ?? c.overall_status}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      {latest ? (
                        <Stack gap="var(--axio-unit-0)">
                          <Text size="sm">
                            {latest.job_title} · {latest.company_name}
                          </Text>
                          {c.submissions.length > 1 && (
                            <Text size="xs" c="dimmed">
                              +{c.submissions.length - 1} more job
                              {c.submissions.length - 1 === 1 ? "" : "s"}
                            </Text>
                          )}
                        </Stack>
                      ) : (
                        <Text size="sm" c="dimmed">
                          Not yet submitted
                        </Text>
                      )}
                    </Table.Td>
                    <Table.Td>{formatDate(c.joined_at)}</Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        </DataTableShell>
      </Stack>
    </Stack>
  );
}
